<template>
    <div id="sensors" :class="$mq" v-if="!isIntegrationSensor">
        <SensorForm v-if="showForm" :sensorObject="sensorSelected" @cancel="hideForm"></SensorForm>

        <div class="search-container" v-if="!showForm">
            <input ref="searchBarSensor" class="search" type="text" @input="searchBarInput($event)" :value="search" />
            <div v-if="search != ''" class="close" @click="cleanSearch()"></div>
        </div>

        <div v-if="filteredSensors.length === 0" class="empty-page">
            <EmptyPage
                @buttonAction="newSensor()"
                customClass="sensors-config"
                :title="$t('config.sensor.empty_page_title')"
                :description="$t('config.sensor.empty_page_description')"
                :buttonText="$t('sensor.new_sensor')"
                buttonClass="link"
            />
        </div>

        <div class="sensors-container hide-scrollbar" v-if="!showForm">
            <sensor-config-card v-for="sensor in filteredSensors" :key="sensor.id" @click.native="selectSensor(sensor)" :sensor="sensor" />
        </div>

        <div v-if="!showForm && filteredSensors.length > 0 && userRoleLevelIsThreeOrMore" @click="newSensor" class="create-btn">
            {{ $t('sensor.new_sensor') }}
        </div>
    </div>
    <div id="sensors" :class="$mq" v-else-if="isIntegrationSensor && !isSensorSelected" class="testo">
        <div class="header-info">
            <div class="text">
                <h3>{{ $t('config.sensor.bond_devices') }}</h3>
            </div>
            <span v-if="isTestoSensor" class="sensor-message">
                <span class="title">{{ $t('config.sensor.connection_key') }}: {{ connectKeys ? connectKeys : 'XXXXXXXXXXX' }}</span>
            </span>
        </div>
        <div class="content-sensors" v-if="(sensors && Object.keys(sensors).length > 0) || (receptors && Object.keys(receptors).length > 0) || (equipment && Object.keys(equipment).length > 0)">
            <div class="tabs-container">
                <Tabs :tabOneText="$t('config.sensor.tab_sensors')" :tabTwoText="$t('config.sensor.tab_receptors')" @switchClick="switchFilter" :selected="filterBy" customClass="testo"> </Tabs>
            </div>
            <div class="sensors-list">
                <template v-if="filterBy == 'tabOne' && !isSensorSelected">
                    <ul class="sensors-list-tree">
                        <li v-if="sensors && Object.keys(sensors.unrelated).length > 0">
                            <details open>
                                <summary>{{ $t('config.sensor.unrelated_sensors') }} ({{ sensors && sensors.unrelated ? Object.keys(sensors.unrelated).length : 0 }})</summary>
                                <ul v-if="sensors && Object.keys(sensors.unrelated).length > 0">
                                    <li v-for="sensor in sensors.unrelated" :key="sensor.id">
                                        <DeviceCard :device="sensor" @selectDeviceRelation="selectDeviceRelation" />
                                    </li>
                                </ul>
                                <ul v-else>
                                    <li>{{ $t('config.sensor.empty_unrelated_sensors') }}</li>
                                </ul>
                            </details>
                        </li>
                        <li v-if="sensors && Object.keys(sensors.related).length > 0">
                            <details open>
                                <summary>{{ $t('config.sensor.related_sensors') }}</summary>
                                <ul v-if="sensors && Object.keys(sensors.related).length > 0">
                                    <li v-for="sensor in sensors.related" :key="sensor.id">
                                        <DeviceCard :device="sensor" @selectDeviceRelation="selectDeviceRelation" :related="equipment[sensor.equipment]" :key="sensor.id" />
                                    </li>
                                </ul>
                                <ul v-else>
                                    <li>{{ $t('config.sensor.empty_related_sensors') }}</li>
                                </ul>
                            </details>
                        </li>
                    </ul>
                </template>
                <template v-if="filterBy == 'tabTwo' && !isSensorSelected">
                    <div class="equipment-container">
                        <span class="equipment-title">{{ $t('config.sensor.tab_receptors') }}</span>
                        <template v-if="receptors && Object.keys(receptors).length > 0">
                            <DeviceCard v-for="receptor in receptors" :key="receptor.id" :device="receptor" type="receptor" />
                        </template>
                        <template v-else>
                            {{ $t('config.sensor.empty_receptors') }}
                        </template>
                    </div>
                </template>
            </div>
        </div>
        <div class="content-sensors" v-else>
            <EmptyPage @buttonAction="refreshSensors()" customClass="sensors-config testo" :description="$t(integrationEmptyPageDescriptionKey)" :buttonText="$t('config.sensor.update')" buttonClass="link" />
        </div>
        <div class="sensors-refresh-btn-container" @click="refreshSensors()">
            <span class="sensors-refresh-btn">
                {{ $t('config.sensor.update') }}
            </span>
        </div>
    </div>
    <div id="sensors-view" class="testo" :class="$mq" v-else-if="isIntegrationSensor && isSensorSelected">
        <div class="header-info">
            <div class="text">
                <h3>{{ $t('config.sensor.bond_devices') }}</h3>
            </div>
        </div>
        <div class="equipment-container">
            <div class="equipment-selected-info">
                <span class="name">{{ $t('config.sensor.sensor_name') }}</span>
                <span class="sn">SN {{ selectedSensor.ref }}</span>
                <div class="section-line"></div>
                <div class="sensor-status-info">
                    <div class="status-item">
                        <i class="i-andy-history i-andy-sm"></i>
                        <div class="status-content">
                            <span class="status-label">{{ $t('config.sensor.devices.last_communication') }}</span>
                            <span class="status-value">{{ formatLastConnection(selectedSensor) }}</span>
                        </div>
                    </div>
                    <div class="status-item">
                        <i class="i-andy-flash i-andy-sm"></i>
                        <div class="status-content">
                            <span class="status-label">{{ $t('config.sensor.batteryLevel') }}</span>
                            <span class="status-value">{{ getBatteryLevel(selectedSensor.metadata && selectedSensor.metadata.battery) }}</span>
                        </div>
                    </div>
                </div>

                <div class="sensor-connectivity">
                    <div class="connectivity-content">
                        <span class="connectivity-label">{{ $t('config.sensor.devices.connection_type') }}</span>
                        <span class="connectivity-value">{{ getConnectivityStatus(selectedSensor.metadata && selectedSensor.metadata.radio) }}</span>
                    </div>
                </div>
                <div class="section-line"></div>
            </div>
            <div class="equipment-selected-actions">
                <span class="subtitle">{{ $t('config.sensor.equipment_assigned') }}</span>
                <div class="equipment-dropdown">
                    <div class="equipment-dropdown-toggle" @click="toggleDropdown">
                        <span class="selected-text">
                            {{ getSelectedEquipmentText() }}
                        </span>
                        <span class="dropdown-arrow" :class="{ open: isDropdownOpen }"></span>
                    </div>
                    <ul class="equipment-list" v-show="isDropdownOpen">
                        <li
                            class="equipment-item"
                            :class="{ selected: selectEquipment === null }"
                            @click="
                                selectEquipment = null
                                changeEquipment()
                                closeDropdown()
                            "
                        >
                            {{ $t('config.sensor.none') }}
                        </li>
                        <li
                            v-for="equipment in availableEquipment"
                            :key="equipment.id"
                            class="equipment-item"
                            :class="{ selected: selectEquipment === equipment.id }"
                            @click="
                                selectEquipment = equipment.id
                                changeEquipment()
                                closeDropdown()
                            "
                        >
                            {{ equipment.name }} {{ selectEquipment === equipment.id ? '(' + $t('config.sensor.assigned') + ')' : '' }}
                        </li>
                    </ul>
                </div>
            </div>
            <div class="equipment-selected-alerts">
                <span class="subtitle">{{ $t('config.sensor.alert_info') }}</span>
                <div class="alert-container">
                    <span class="icon-info"></span>
                    <span class="alert-text" v-if="selectEquipment && selectEquipment !== 'null' && equipment[selectEquipment] && equipment[selectEquipment].alert">
                        {{ equipment[selectEquipment].alert }}
                    </span>
                    <span class="alert-text" v-else>{{ $t('config.sensor.empty_alerts') }}</span>
                </div>
            </div>
            <div v-if="userRoleLevelIsEightOrMore" class="btn-delete-sensor" @click="handleDeleteSensor">{{ $t('sensor.remove_sensor') }}</div>
            <div class="equipment-selected-act-btn">
                <div class="btn-save" @click="saveEquipmentChanges">{{ $t('config.sensor.save') }}</div>
                <div class="btn-cancel" @click="cancelEquipmentChanges">{{ $t('config.sensor.cancel') }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import SensorForm from '@/components/domain/config/forms/sensorForm.vue'
import SensorConfigCard from '@/components/domain/sensor/sensorConfigCard.vue'
import EmptyPage from '../../layout/EmptyPage.vue'
import Tabs from '@/components/domain/ui/Tabs.vue'
import DeviceCard from '@/components/domain/config/sensors/DeviceCard.vue'
import { RBE_PREFIXES } from '@/constants'

export default {
    name: 'sensor',
    components: { SensorConfigCard, SensorForm, EmptyPage, Tabs, DeviceCard },
    data() {
        return {
            timerSearch: false,
            search: '',
            showForm: false,
            sensorSelected: false,
            filterBy: 'tabOne',
            showConnectionKey: false,
            isSensorSelected: false,
            selectedSensor: null,
            selectEquipment: null,
            previousEquipmentState: null,
            isDropdownOpen: false,
            unixSecondsMax: 1e11
        }
    },
    computed: {
        receptors() {
            if (this.isIntegrationSensor) {
                return this.$store.getters['sensor/getTestoReceptors']()
            }
            return []
        },
        sensors() {
            if (this.isIntegrationSensor) {
                return this.$store.getters['sensor/getTestoSensors']()
            }
            return this.$store.getters['sensor/getList']()
        },
        equipment() {
            if (this.isIntegrationSensor) {
                return this.$store.getters['sensor/getTestoEquipment']()
            }
            return []
        },
        connectKeys() {
            if (this.isTestoSensor) {
                const config = this.$store.getters['login/getConfig']
                return config.testo_connected_keys ? config.testo_connected_keys : false
            }
            return false
        },
        isIntegrationSensor() {
            return this.sensorType === 'testo' || this.sensorType === 'rmoni'
        },
        isTestoSensor() {
            return this.sensorType === 'testo'
        },
        integrationEmptyPageDescriptionKey() {
            if (this.sensorType === 'rmoni') {
                return 'config.sensor.empty_page_description_rmoni'
            }
            return 'config.sensor.empty_page_description_testo'
        },
        filteredSensors() {
            if (this.sensors) {
                // filter templates by search
                if (this.search != '') {
                    //search by name and id

                    const filteredSensors = Object.values(this.sensors).filter((sensor) => {
                        return sensor.name.toLowerCase().includes(this.search.toLowerCase()) || sensor.ref.toLowerCase().includes(this.search.toLowerCase())
                    })

                    return filteredSensors
                } else {
                    const allSensors = Object.values(this.sensors)

                    return allSensors
                }
            }
        },
        userRoleLevelIsThreeOrMore() {
            const userInfo = this.$store.getters['loginUser/getUserInformation']
            const userRoleId = userInfo.rol

            const userRoleLevel = this.$store.getters['employee/getRol'](userRoleId).level

            return userRoleLevel >= 3
        },
        userRoleLevelIsEightOrMore() {
            const userInfo = this.$store.getters['loginUser/getUserInformation']
            const userRoleId = userInfo.rol

            const userRoleLevel = this.$store.getters['employee/getRol'](userRoleId).level

            return userRoleLevel >= 8
        },
        sensorType() {
            //To validate if the sensor is testo or dotbox
            const config = this.$store.getters['login/getConfig']
            return config && config.sensor && config.sensor.type ? config.sensor.type : false
        },
        availableEquipment() {
            if (!this.equipment || !this.sensors) return []

            // Obtener todos los equipment IDs que ya están asignados a otros sensores
            const assignedEquipmentIds = new Set()
            const currentSensorId = this.selectedSensor && this.selectedSensor.id ? this.selectedSensor.id : null

            // Revisar sensores relacionados
            if (this.sensors.related) {
                Object.values(this.sensors.related).forEach((sensor) => {
                    if (sensor.equipment && sensor.id !== currentSensorId) {
                        assignedEquipmentIds.add(sensor.equipment)
                    }
                })
            }

            // Revisar sensores no relacionados
            if (this.sensors.unrelated) {
                Object.values(this.sensors.unrelated).forEach((sensor) => {
                    if (sensor.equipment && sensor.id !== currentSensorId) {
                        assignedEquipmentIds.add(sensor.equipment)
                    }
                })
            }

            // Filtrar equipment disponibles (excluir los ya asignados) y ordenar por name
            return Object.fromEntries(
                Object.entries(this.equipment)
                    .filter(([equipmentId, equipment]) => !assignedEquipmentIds.has(equipmentId))
                    .sort((a, b) => a[1].name.localeCompare(b[1].name))
            )
        },
        account() {
            return this.$store.getters['login/getAccount']
        },
        location() {
            return this.$store.getters['login/getLocation']
        },
        locationInformation() {
            return this.$store.getters['login/getLocation']
        }
    },
    methods: {
        changeEquipment() {
            // Si se selecciona "ninguno", establecer como null
            //Si es un receptor no se puede seleccionar ninguno
            if (this.selectedSensor.type === 'receptor') {
                this.selectEquipment = null
                return
            }
            if (this.selectEquipment === 'null') {
                this.selectEquipment = null
            }
        },
        saveEquipmentChanges() {
            const rel_id = this.selectedSensor.rel_l_id

            this.$store
                .dispatch('sensor/updateSensorEquipment', {
                    sensor_id: this.selectedSensor.id,
                    equipment_id: this.selectEquipment,
                    rel_id: rel_id
                })
                .then(() => {
                    // ok
                })
                .catch((error) => {
                    console.error('Error al guardar cambios:', error)
                    this.$toast && this.$toast.error(this.$t('config.sensor.error_saving') || 'Error al guardar cambios')
                })

            // Limpiar el estado y volver a la vista anterior
            this.isSensorSelected = false
            this.selectedSensor = null
            this.selectEquipment = null
            this.previousEquipmentState = null
        },
        cancelEquipmentChanges() {
            // Cancelar cambios y volver a la vista anterior
            this.isSensorSelected = false
            this.selectedSensor = null
            this.selectEquipment = null
            this.previousEquipmentState = null
        },
        selectDeviceRelation(device, type) {
            this.isSensorSelected = true
            if (type === 'sensor') {
                this.selectedSensor = device
                // Guardar el estado anterior del sensor para comparar después
                this.previousEquipmentState = device.equipment
                // Inicializar el select con el equipo actual del sensor o null si no tiene
                if (device.equipment && this.equipment[device.equipment]) {
                    this.selectEquipment = device.equipment
                } else {
                    this.selectEquipment = null
                }
            }
        },
        switchFilter(type) {
            this.filterBy = type === 'tabOne' ? 'tabOne' : 'tabTwo'
        },
        getDatabaseParam() {
            const username = this.locationInformation.username && this.locationInformation.username.split('.')[0] ? this.locationInformation.username.split('.')[0] : null
            if (username && RBE_PREFIXES.includes(username.toLowerCase())) {
                return 1
            }
            return -1
        },
        refreshSensors() {
            this.$overlay.loading()
            const self = this
            const syncPromise = this.sensorType === 'rmoni'
                ? this.$store.dispatch('sensor/syncWithRmoniApi')
                : this.$store.dispatch('sensor/syncWithTestoApi', {
                    account_id: this.account,
                    location_id: this.location.id,
                    c: this.getDatabaseParam()
                })

            syncPromise.then(() => {
                Promise.all([
                    self.$store.dispatch('sensor/loadTestoDevices'),
                    self.$store.dispatch('sensor/loadList')
                ]).then(() => {
                    self.$overlay.hide()
                })
            })
        },
        searchBarInput(e) {
            const self = this
            if (this.timerSearch) {
                clearTimeout(this.timerSearch)
                this.timerSearch = null
            }
            this.timerSearch = setTimeout(() => {
                self.search = e.target.value
                // if (self.$mq != 'portrait') {
                //     self.selectedCat = self.categories['all']
                // }
            }, 800)
        },
        newSensor() {
            this.sensorSelected = false
            this.showForm = true
        },
        hideForm() {
            this.showForm = false
            this.sensorSelected = false
            // this.$overlay.hide()
        },
        selectSensor(sensor) {
            this.sensorSelected = sensor
            this.showForm = true
        },
        cleanSearch() {
            this.search = ''
        },
        load() {
            if (!this.isIntegrationSensor) {
                this.$store.dispatch('sensor/loadList').then(() => {})
            } else {
                this.$overlay.loading()
                Promise.all([
                    this.$store.dispatch('sensor/loadTestoDevices'),
                    this.$store.dispatch('sensor/loadList')
                ]).then(() => {
                    this.$overlay.hide()
                })
            }
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen
        },
        closeDropdown() {
            this.isDropdownOpen = false
        },
        getSelectedEquipmentText() {
            if (this.selectEquipment === null) {
                return this.$t('config.sensor.none')
            }
            if (this.equipment && this.equipment[this.selectEquipment]) {
                return this.equipment[this.selectEquipment].name
            }
            return this.$t('config.sensor.none')
        },
        formatLastConnection(device) {
            const lastActivity = this.getLastActivity(device)
            return lastActivity ? lastActivity.format('DD/MM/YYYY - HH:mm') : '-'
        },
        lastValueDate(source) {
            if (source && source.last_value && source.last_value.date != null && source.last_value.date !== '') {
                return source.last_value.date
            }

            return null
        },
        parseLastActivity(value) {
            if (value === null || value === undefined || value === '' || value === 'null') {
                return null
            }

            if (typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(String(value).trim()))) {
                const timestamp = Number(value)
                if (!Number.isFinite(timestamp) || timestamp <= 0) {
                    return null
                }

                const parsedUnix = moment(timestamp < this.unixSecondsMax ? timestamp * 1000 : timestamp)
                return parsedUnix.isValid() ? parsedUnix : null
            }

            const parsed = moment(value)
            return parsed.isValid() ? parsed : null
        },
        findLinkedSensor(device) {
            const sensors = this.$store.getters['sensor/getList']()
            if (!device || !sensors) {
                return null
            }

            if (device.id && sensors[device.id]) {
                return sensors[device.id]
            }

            if (!device.ref) {
                return null
            }

            return Object.values(sensors).find((sensor) => sensor && sensor.ref === device.ref) || null
        },
        getLastActivity(device) {
            const dates = []
            const deviceDate = this.lastValueDate(device)
            if (deviceDate !== null) {
                dates.push(deviceDate)
            }

            const linkedDate = this.lastValueDate(this.findLinkedSensor(device))
            if (linkedDate !== null) {
                dates.push(linkedDate)
            }

            const moments = dates.map((date) => this.parseLastActivity(date)).filter(Boolean)
            if (moments.length > 0) {
                return moments.reduce((latest, current) => (current.isAfter(latest) ? current : latest))
            }

            const lastCommunication = device && device.metadata ? device.metadata.last_communication : null
            return this.parseLastActivity(lastCommunication)
        },
        getBatteryLevel(battery) {
            if (battery === null || battery === undefined || battery === '') {
                return '-'
            }
            return `${battery}%`
        },
        getConnectivityStatus(radio) {
            let level = 'out'

            if (radio >= 55) {
                level = 'full'
            } else if (radio >= 5) {
                level = 'low'
            }

            return this.$t(`config.sensor.devices.${level}`)
        },
        handleDeleteSensor() {
            const self = this
            const sensorName = this.selectedSensor && this.selectedSensor.ref ? this.selectedSensor.ref : ''
            const equipmentName = this.selectEquipment && this.equipment && this.equipment[this.selectEquipment] ? this.equipment[this.selectEquipment].name : this.$t('config.sensor.none')

            // Build confirmation message with sensor name and equipment (plain text, no HTML)
            const confirmMessage = `${this.$t('popup.confirm.remove_sensor_question')}\n\n${this.$t('config.sensor.sensor_name')}: ${sensorName}\n${this.$t('config.sensor.equipment_assigned')}: ${equipmentName}`

            this.$popup.open({
                type: 'messageNewDesing',
                title: this.$t('sensor.remove_sensor'),
                message: confirmMessage,
                textSave: this.$t('menu_aux.action.delete'),
                textCancel: this.$t('popup.confirm.text_cancel'),
                customClass: 'delete-sensor-popup',
                callSave: function () {
                    self.$overlay.loading()
                    self.$store
                        .dispatch('sensor/deleteTestoSensor', self.selectedSensor.id)
                        .then(() => {
                            self.$popup.close()
                            self.$overlay.hide()
                            self.$snackbar.success({
                                duration: 3000,
                                closeable: true,
                                message: self.$t('config.sensor.remove_success', { name: sensorName })
                            })
                            // Reset state and go back to sensor list
                            self.isSensorSelected = false
                            self.selectedSensor = null
                            self.selectEquipment = null
                            self.previousEquipmentState = null
                            // Reload sensor list
                            self.load()
                        })
                        .catch((error) => {
                            console.error('Error deleting sensor:', error)
                            self.$overlay.hide()
                            self.$snackbar.error({
                                duration: 3000,
                                closeable: true,
                                message: self.$t('config.sensor.remove_error')
                            })
                        })
                },
                callCancel: function () {
                    self.$popup.close()
                }
            })
        }
    },

    created() {
        this.load()

        if (this.$store.getters['sensor/getCreateState']) {
            this.sensorSelected = false
            this.showForm = true
            this.$store.commit('sensor/setCreateState', false)
        }
    }
}
</script>

<style lang="scss">
#sensors,
#sensors-view {
    width: 100%;
    height: 100%;
    padding: 10px;
    overflow: hidden;
    position: relative;

    .search-container {
        position: relative;
        width: 100%;

        .search {
            @include display-flex();
            @include align-items(center);
            @include background($image: img('search_neutro.svg'), $size: 25px, $position: left 12px center);
            @include border-radius(4px);
            @include font-size(ml);
            height: 45px;
            width: 100%;
            background-color: #fff;
            padding: 0 60px 0 45px;
            border-bottom: none;
            font-family: $text;
            cursor: text;
        }

        .close {
            @include border-radius(4px);
            @include background($image: img('close_dark.svg'), $size: 12px, $position: center center);
            width: 33px;
            height: 33px;
            position: absolute;
            top: 7px;
            right: 10px;
            background-color: $neutro-t50;
            cursor: pointer;
        }
    }

    .empty-page {
        height: calc(100% - 100px);
        display: grid;
        place-items: center;
    }

    .sensors-container {
        padding-top: 10px;
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(auto-fill, minmax(390px, 1fr));
        height: auto;
        max-height: calc(100% - 110px);
        overflow: scroll;
        grid-template-rows: 80px;
    }

    .create-btn {
        @include display-flex();
        @include align-items(center);
        @include align-self(center);
        @include justify-content(center);
        @include font-size(ml);
        @include border-radius(4px);
        width: fit-content;
        height: 50px;
        padding: 0 20px;
        background-color: $main;
        font-family: $text-bold;
        color: #fff;
        cursor: pointer;
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translateX(-50%);
    }

    &.portrait {
        padding: unset;

        .sensors-container {
            grid-template-columns: repeat(auto-fill, minmax(335px, 1fr));
            max-height: calc(100% - 110px) !important;
        }

        .create-btn {
            bottom: 0px;
            @include font-size(sm);
            background-color: $main-dark;
            font-size: 16px;
        }
    }

    &.testo {
        display: grid;
        grid-template-areas:
            'header'
            'content'
            'refresh';
        grid-template-rows: auto 1fr auto;
        gap: 10px;
        height: 100%;
        overflow: hidden;

        .header-info {
            font-family: $text;
            @include display-flex();
            @include align-items(flex-start);
            @include flex-direction(column);
            grid-area: header;
            margin-bottom: 0;

            .text {
                width: calc(100% - 45px);
                @include display-flex();
                @include border-radius(4px);
                @include flex-direction(column);

                color: $neutro-s80;

                h3 {
                    @include font-size(24px);
                    font-family: $text-bold;
                }

                p {
                    @include font-size(18px);
                    font-family: $text;
                }
            }

            .sensor-message {
                @include display-flex();
                @include flex-direction(column);
                @include align-items(flex-start);
                @include justify-content(space-between);
                background: $info-light;
                padding: 8px;
                border-radius: 4px;
                border: 1px solid $info;
                width: 100%;

                .title {
                    width: 100%;
                    @include display-flex();
                    @include justify-content(space-between);
                    @include align-items(center);
                    @include font-size(14px);
                    font-family: $text-bold;
                    color: $neutro-s80;
                }

                .message {
                    width: 100%;

                    .connection-key {
                        @include font-size(16px);
                        font-family: $text-bold;
                        color: $neutro-s80;
                    }
                }
            }
        }

        .content-sensors {
            grid-area: content;
            overflow: hidden;
            display: flex;
            flex-direction: column;

            .tabs-container {
                @include display-flex();
                @include justify-content(space-between);
                @include align-items(center);
                @include border-radius(4px);
                width: 100%;
                height: 45px;
                padding: 3px;
                margin-bottom: 10px;
            }
        }

        .equipment-container {
            @include display-flex();
            @include flex-direction(column);
            gap: 16px;

            .equipment-selected-info {
                @include display-flex();
                @include flex-direction(column);
                gap: 16px;

                .name {
                    @include font-size(18px);
                    font-family: $text;
                    color: #5b5c5f;
                }

                .sn {
                    @include font-size(18px);
                    font-family: $text-bold;
                    color: $neutro-s60;
                    text-transform: uppercase;
                }
                .section-line {
                    width: 100%;
                    height: 1px;
                    background-color: #e0deda;
                }
                .sensor-status-info {
                    @include display-flex();
                    gap: 24px;

                    .status-item {
                        @include display-flex();
                        @include align-items(flex-start);
                        flex-direction: column;
                        gap: 8px;
                        flex: 1;
                        grid-row: 1 / span 1;
                        grid-column: 1 / span 1;
                        .status-icon {
                            width: 20px;
                            height: 20px;
                            flex-shrink: 0;
                            color: $neutro-s60;
                            font-size: 20px;
                        }

                        .status-content {
                            @include display-flex();
                            @include flex-direction(column);

                            .status-label {
                                @include font-size(18px);
                                font-family: $text-bold;
                                color: $neutro-s80;
                            }

                            .status-value {
                                @include font-size(18px);
                                font-family: $text;
                                color: $neutro-s60;
                            }
                        }
                    }
                }

                .sensor-connectivity {
                    @include display-flex();
                    @include align-items(flex-start);
                    @include flex-direction(column);
                    gap: 8px;

                    .connectivity-icon {
                        width: 24px;
                        height: 24px;
                        flex-shrink: 0;
                        color: $neutro-s60;
                        font-size: 20px;
                    }

                    .connectivity-content {
                        @include display-flex();
                        @include flex-direction(column);
                        gap: 4px;

                        .connectivity-label {
                            @include font-size(18px);
                            font-family: $text-bold;
                        }

                        .connectivity-value {
                            @include font-size(18px);
                            font-family: $text;
                            color: $neutro-s60;
                        }
                    }
                }
            }

            .subtitle {
                @include font-size(18px);
                font-family: $text;
                color: #5b5c5f;
            }

            .equipment-selected-actions {
                @include display-flex();
                @include flex-direction(column);
                gap: 4px;

                .equipment-dropdown {
                    position: relative;
                    width: 100%;

                    .equipment-dropdown-toggle {
                        @include display-flex();
                        @include justify-content(space-between);
                        @include align-items(center);
                        @include font-size(18px);
                        font-family: $text;
                        color: #5b5c5f;
                        background-color: $neutro-light;
                        border: 1px solid $neutro-t30;
                        border-radius: 4px;
                        padding: 8px;
                        cursor: pointer;
                        transition: all 0.2s ease;

                        .selected-text {
                            flex: 1;
                            text-align: left;
                        }

                        .dropdown-arrow {
                            width: 20px;
                            height: 20px;
                            position: relative;
                            transition: transform 0.2s ease;

                            &::before {
                                content: '';
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                width: 0;
                                height: 0;
                                border-left: 6px solid transparent;
                                border-right: 6px solid transparent;
                                border-top: 8px solid #5b5c5f;
                                transition: all 0.2s ease;
                            }

                            &.open {
                                transform: rotate(180deg);
                            }
                        }
                    }

                    .equipment-list {
                        position: absolute;
                        top: 100%;
                        left: 0;
                        right: 0;
                        list-style: none;
                        padding: 0;
                        margin: 0;
                        border-radius: 4px;
                        overflow: hidden;
                        border: 1px solid $neutro-t30;
                        background-color: #fff;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        z-index: 1000;
                        max-height: 200px;
                        overflow-y: auto;

                        .equipment-item {
                            @include font-size(18px);
                            font-family: $text;
                            color: #5b5c5f;
                            background-color: $neutro-light;
                            padding: 8px;
                            cursor: pointer;
                            transition: all 0.2s ease;
                            border-bottom: 1px solid $neutro-t20;

                            &:last-child {
                                border-bottom: none;
                            }

                            &:hover {
                                background-color: $main-t90;
                                color: $neutro-s80;
                            }

                            &.selected {
                                color: $neutro-s10;
                            }

                            // Estilo alternado para elementos pares e impares
                            &:nth-child(even) {
                                background-color: $main-t90;
                            }

                            &:nth-child(even):hover {
                                background-color: $main-t80;
                            }
                        }
                    }
                }
            }

            .equipment-selected-alerts {
                @include display-flex();
                @include flex-direction(column);
                gap: 4px;

                .alert-container {
                    @include display-flex();
                    @include align-items(center);
                    gap: 8px;
                    border-radius: 4px;
                    border: 1px solid $warning-light;
                    background-color: $warning-t80;
                    padding: 8px;

                    .icon-info {
                        @include background($image: img('alert_warn.svg'), $size: 16px, $position: center center);
                        width: 30px;
                        height: 30px;
                    }

                    .alert-text {
                        @include font-size(16px);
                        font-family: $text;
                        color: $neutro-s60;
                    }
                }
            }
            .btn-delete-sensor {
                @include font-size(16px);
                font-family: $text-bold;
                color: $error-dark;
                padding: 16px 8px;
                width: 100%;
                text-align: center;
                cursor: pointer;
                background-color: transparent;
                border: none;
                border-top: 1px solid #e0deda;
                border-bottom: 1px solid #e0deda;
            }
            .equipment-selected-act-btn {
                @include display-flex();
                @include flex-direction(column);
                @include align-items(center);
                gap: 16px;

                .btn-save {
                    border-radius: 4px;
                    @include font-size(16px);
                    font-family: $text-bold;
                    color: $color-white;
                    cursor: pointer;
                    background-color: #384673;
                    padding: 8px;
                    width: 100%;
                    text-align: center;
                }

                .btn-cancel {
                    border-radius: 4px;
                    @include font-size(16px);
                    font-family: $text-bold;
                    color: #384673;
                    cursor: pointer;
                    background-color: #e8eaef;
                    padding: 8px;
                    width: 100%;
                    text-align: center;
                }

                .btn-delete {
                    border-radius: 4px;
                    @include font-size(16px);
                    font-family: $text-bold;
                    color: $color-white;
                    cursor: pointer;
                    background-color: $error-dark;
                    padding: 8px;
                    width: 100%;
                    text-align: center;
                    margin-top: 8px;
                }
            }
        }

        .sensors-list {
            width: 100%;
            height: 100%;
            overflow-y: auto;
            flex: 1;
            padding-right: 10px;

            .equipment-title {
                @include font-size(18px);
                font-family: $text-bold;
                color: $neutro-s80;
            }

            .equipment-container {
                @include display-flex();
                @include flex-direction(column);
                gap: 16px;
            }

            .sensors-list-tree {
                summary {
                    @include font-size(18px);
                    font-family: $text-bold;
                    color: $neutro-s80;
                    cursor: pointer;
                    margin-bottom: 16px;
                }

                li {
                    margin-bottom: 16px;
                }
            }
        }

        .sensors-refresh-btn {
            margin-bottom: 10px;
            width: 100%;
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            @include font-size(18px);
            @include border-radius(4px);
            background-color: $main-dark;
            padding: 8px 24px;
            color: $color-white;
            font-family: $text-bold;
            cursor: pointer;
            margin-top: 0;
            grid-area: refresh;
        }

        &.desktop,
        &.landscape {
            .header-info {
                @include align-items(center);
                .text {
                    width: fit-content;
                    @include justify-content(center);
                }
                .sensor-message {
                    padding: 8px 24px;
                    @include justify-content(center);
                    width: fit-content;
                    .title {
                        @include font-size(14px);
                    }
                }
            }

            .tabs-container {
                width: 100%;
                @include justify-content(center);
            }

            .sensors-list {
                padding: 4px 16px;
            }

            .sensors-refresh-btn-container {
                width: 100%;
                @include display-flex();
                @include align-items(center);
                @include justify-content(center);

                .sensors-refresh-btn {
                    width: fit-content;
                }
            }

            .equipment-container {
                padding: 4px 16px;
            }

            .equipment-selected-act-btn {
                @include flex-direction(row);
            }
        }
    }

    &.portrait {
        &.testo {
            .header-info {
                .text {
                    h3 {
                        @include font-size(ml);
                    }
                }
            }
        }

        .content-sensors {
            .tabs-container {
                .text {
                    @include font-size(s);
                }
            }
        }
    }
}
</style>
